import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

import { AuthorizationService } from "./authorization.service";

/**
 * Retrieves the Excel reports served by `api/Reports`.
 *
 * Every action on the server's `ReportsController` is gated by `AuthorizationFilter`, and a plain
 * browser navigation cannot carry the `Authorization` header the filter requires - it is answered
 * with 401. Reports are therefore requested here as authenticated XHRs and handed to the browser
 * as a blob, following the same pattern as `DocumentService.downloadPDF`.
 */
@Injectable({
  providedIn: "root", // Ensures this service is available globally through dependency injection
})
export class ReportService {
  private reportBaseUrl: string = window.document.location.origin + "/api/Reports/";

  constructor(
    private _http: HttpClient,
    private _authorizationService: AuthorizationService
  ) {}

  /**
   * Builds the absolute URL of a report action, e.g. `reportUrl('UserReport')`.
   * @param action The `ReportsController` action name.
   */
  public reportUrl(action: string): string {
    return this.reportBaseUrl + action;
  }

  /**
   * Requests a report with the caller's bearer token attached.
   *
   * The URL is passed through untouched so callers keep full control of their filter query
   * string; use {@link reportUrl} to build the base.
   *
   * @param url Absolute URL of an `api/Reports` action, including any query string.
   * @returns The full response, so the caller can read the server-supplied file name from
   *          `Content-Disposition`.
   */
  public downloadReport(url: string): Observable<HttpResponse<Blob>> {
    let headers = new HttpHeaders();
    headers = headers.append(
      "Authorization",
      `Bearer ${this._authorizationService.getAuthorization()}`
    );

    return this._http
      .get(url, {
        headers: headers,
        responseType: "blob",
        observe: "response",
      })
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  /**
   * Saves a report response to disk under the file name the server supplied.
   * @param response A response returned by {@link downloadReport}.
   * @param fallbackFileName Used only when the response carries no usable `Content-Disposition`.
   */
  public saveAsFile(response: HttpResponse<Blob>, fallbackFileName: string): void {
    const fileName = this.extractFileName(
      response.headers.get("Content-Disposition"),
      fallbackFileName
    );

    const blobUrl = window.URL.createObjectURL(response.body);
    const link = window.document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  }

  /**
   * Reads the file name out of a `Content-Disposition` header, preferring the RFC 5987 encoded
   * form when the server supplies one. Any directory component is dropped.
   */
  private extractFileName(contentDisposition: string, fallbackFileName: string): string {
    if (!contentDisposition) {
      return fallbackFileName;
    }

    const encodedMatch = contentDisposition.match(/filename\*\s*=\s*(?:UTF-8'[^']*')?([^;]+)/i);
    if (encodedMatch && encodedMatch[1]) {
      const encodedFileName = this.unwrapQuotes(encodedMatch[1]);
      try {
        return this.stripPath(decodeURIComponent(encodedFileName)) || fallbackFileName;
      } catch {
        // Not valid percent-encoding - fall through to the plain filename parameter.
      }
    }

    const plainMatch = contentDisposition.match(/filename\s*=\s*("[^"]*"|[^;]+)/i);
    if (plainMatch && plainMatch[1]) {
      return this.stripPath(this.unwrapQuotes(plainMatch[1])) || fallbackFileName;
    }

    return fallbackFileName;
  }

  private unwrapQuotes(value: string): string {
    return value.trim().replace(/^"(.*)"$/, "$1");
  }

  private stripPath(fileName: string): string {
    return fileName.replace(/^.*[\\/]/, "").trim();
  }

  /**
   * Translates a failed report request into a message that can be shown to the user.
   * The response body is a blob, so the status is all there is to go on.
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error("Error downloading report:", error);

    let message = "The report could not be generated. Please try again.";
    if (error.status === 401 || error.status === 403) {
      message = "You are not authorized to download this report. Please sign in again.";
    } else if (error.status === 0) {
      message = "The report request could not reach the server. Please check your connection and try again.";
    }

    return throwError(() => new Error(message));
  }
}
